import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, Plus } from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

const FIELDS = [['legal_name', 'Legal name'], ['trading_name', 'Trading name'], ['abn', 'ABN'], ['acn', 'ACN'], ['entity_type', 'Entity type'],
  ['registered_address', 'Registered address'], ['business_address', 'Business address'], ['contact_phone', 'Contact phone'], ['contact_email', 'Contact email'],
  ['business_activities', 'Business activities'], ['bas_cycle', 'BAS reporting frequency'], ['fy_end', 'Financial year end']];

const AddEntity = () => {
  const [v, setV] = useState({ entity_type: 'company' });
  const send = async () => {
    try { await api.post('/api/portal/entity-requests', v); toast.success('Request sent – we will set it up and let you know'); setV({ entity_type: 'company' }); } catch (e) { toast.error(e.response?.data?.detail || 'Could not send'); }
  };
  return (
    <div className="space-y-2 border-t border-border pt-6" data-testid="add-entity-form">
      <p className="text-sm font-medium flex items-center gap-2"><Plus className="w-4 h-4" />Add an entity</p>
      <div className="grid sm:grid-cols-4 gap-2">
        <Input placeholder="Entity name" value={v.name || ''} onChange={(e) => setV({ ...v, name: e.target.value })} data-testid="add-entity-name" />
        <select className="h-9 rounded-md border border-input bg-background px-2 text-sm" value={v.entity_type} onChange={(e) => setV({ ...v, entity_type: e.target.value })} data-testid="add-entity-type">
          {['company', 'trust', 'partnership', 'smsf', 'sole_trader'].map((t) => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
        </select>
        <Input placeholder="ABN (optional)" value={v.abn || ''} onChange={(e) => setV({ ...v, abn: e.target.value })} data-testid="add-entity-abn" />
        <Input placeholder="Your relationship (e.g. director)" value={v.relationship || ''} onChange={(e) => setV({ ...v, relationship: e.target.value })} data-testid="add-entity-relationship" />
      </div>
      <Button size="sm" variant="outline" onClick={send} data-testid="add-entity-submit">Send request</Button>
    </div>
  );
};

export const EntityDetails = () => {
  const [d, setD] = useState(null);
  const [form, setForm] = useState({});
  const [oh, setOh] = useState({ name: '', role: 'director' });
  const load = useCallback(() => api.get('/api/portal/entity-details').then(({ data }) => { setD(data); setForm(data.details); }), []);
  useEffect(() => { load(); }, [load]);
  if (!d) return null;

  const save = async () => {
    const body = Object.fromEntries(Object.entries(form).filter(([k, v]) => v !== d.details[k]));
    if (oh.name) body.officeholders = { add: [oh] };
    try {
      const { data } = await api.post('/api/portal/entity-details', { ...body, _version: d.version });
      toast.success(data.pending ? 'Sent for review' : 'Saved');
      setOh({ name: '', role: 'director' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
  };

  return (
    <Card data-testid="entity-details">
      <CardHeader>
        <CardTitle className="text-base">Entity details</CardTitle>
        <div className="flex items-center gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm" data-testid="entity-asic-ato-banner">
          <AlertTriangle className="w-4 h-4 text-amber-600" />{d.banner}
        </div>
        <p className="text-xs text-muted-foreground">Trading name, business address, contacts and activities update straight away; identifiers, registrations and officeholders are reviewed by your accountant.</p>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {FIELDS.map(([k, l]) => (
            <div key={k} className="space-y-1">
              <Label className="text-xs">{l}</Label>
              <Input value={form[k] ?? ''} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`entity-${k}`} />
            </div>
          ))}
          {['gst_registered', 'payg_withholding'].map((k) => (
            <label key={k} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.checked })} data-testid={`entity-${k}`} />
              {k === 'gst_registered' ? 'Registered for GST' : 'Registered for PAYG withholding'}</label>
          ))}
        </div>
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Directors, shareholders, trustees, beneficiaries</p>
          {d.officeholders.map((o) => <p key={o.id} className="text-sm" data-testid={`officeholder-${o.id}`}>{o.name} · <span className="capitalize">{o.role}</span></p>)}
          <div className="flex gap-2 mt-2 max-w-md">
            <Input placeholder="Add a person" value={oh.name} onChange={(e) => setOh({ ...oh, name: e.target.value })} data-testid="officeholder-name" />
            <select className="h-9 rounded-md border border-input bg-background px-2 text-sm" value={oh.role} onChange={(e) => setOh({ ...oh, role: e.target.value })} data-testid="officeholder-role">
              {['director', 'shareholder', 'trustee', 'beneficiary', 'partner', 'member', 'secretary'].map((r) => <option key={r}>{r}</option>)}
            </select>
          </div>
        </div>
        <Button onClick={save} data-testid="entity-save">Save / send for review</Button>
        <AddEntity />
      </CardContent>
    </Card>
  );
};
